import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../runtimeConfig';
import InsightsAtGPT from './InsightsAtGPT';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);
const gridTheme = themeQuartz;

const SurveyInsights = () => {
  const navigate = useNavigate();
  const { userAttributes } = useAuth();
  const gridRef = useRef();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Table state
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    project_codes: [],
    brand_names: [],
    survey_categories: [],
    occasion_nos: [],
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Filter state
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedMoment, setSelectedMoment] = useState('All');
  
  // Available projects
  const [availableProjects, setAvailableProjects] = useState([]);

  // Project info (authoritative date window)
  const [projectInfo, setProjectInfo] = useState(null);
  
  // Column visibility state
  const [columnVisibilityOpen, setColumnVisibilityOpen] = useState(false);

  // Fetch available projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) return;
        
        const response = await axios.get(
          `${API_URL}/api/analytics-views/projects`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          // Server already enforces access control via Cognito groups
          // (admin => all projects; otherwise scoped to the user's group list).
          // Trust it as-is. Re-filtering here double-applied the group check and
          // could wipe the dropdown on any client/server projectAccess skew.
          setAvailableProjects(response.data.projects);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
        if (userAttributes?.projectAccess) {
          setAvailableProjects(userAttributes.projectAccess);
        }
      }
    };
    
    if (userAttributes) {
      fetchProjects();
    }
  }, [userAttributes]);

  // Close column visibility dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnVisibilityOpen && !event.target.closest('.column-visibility-container')) {
        setColumnVisibilityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [columnVisibilityOpen]);

  // Generate column definitions
  const generateColumnsFromData = useCallback((data) => {
    if (!data || data.length === 0) return [];
    
    const firstRow = data[0];
    const columns = [];
    
    // Priority columns - always first
    const priorityFields = ['session_id', 'responder_id', 'project_code', 'question_id', 'created_at'];
    
    // Add priority columns first with special config
    priorityFields.forEach(field => {
      if (field in firstRow) {
        const colDef = {
          field,
          headerName: field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          width: field === 'session_id' ? 200 : 150,
        };
        
        // Date filtering
        if (field === 'created_at') {
          colDef.filter = 'agDateColumnFilter';
          colDef.filterParams = {
            buttons: ['apply', 'reset'],
            closeOnApply: true,
          };
          colDef.valueFormatter = (params) => {
            if (!params.value) return '';
            return new Date(params.value).toLocaleString();
          };
        } else {
          // ID columns - text filter with apply/reset buttons
          colDef.filter = 'agTextColumnFilter';
          colDef.filterParams = {
            buttons: ['apply', 'reset'],
            closeOnApply: true,
          };
        }
        
        columns.push(colDef);
      }
    });
    
    // Add remaining columns dynamically from the data
    Object.keys(firstRow).forEach(field => {
      if (priorityFields.includes(field)) return; // Skip priority fields
      
      const value = firstRow[field];
      const colDef = {
        field,
        headerName: field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        width: 150,
        filter: true, // Default filter
      };
      
      // Smart type detection and configuration with apply/reset buttons
      if (field.includes('confidence') || field.includes('_no')) {
        // Numeric columns - use Number Filter
        colDef.filter = 'agNumberColumnFilter';
        colDef.filterParams = {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        };
        if (field.includes('confidence')) {
          colDef.valueFormatter = (params) => {
            return params.value ? `${(params.value * 100).toFixed(1)}%` : 'N/A';
          };
        }
        colDef.width = 120;
      } else if (field === 'brand_name' || field === 'product_category' || field === 'moment_id' || 
                 field === 'survey_category' || field === 'language') {
        // Categorical columns - use text filter with buttons
        colDef.filter = 'agTextColumnFilter';
        colDef.filterParams = {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        };
        colDef.width = 150;
      } else if (field.includes('input') || field.includes('question_text')) {
        // Text columns - use text filter with buttons
        colDef.filter = 'agTextColumnFilter';
        colDef.filterParams = {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        };
        colDef.width = 250;
        colDef.wrapText = true;
        colDef.autoHeight = true;
      } else if (field === 'analytics_values_complete') {
        // Analytics JSON - Show raw text, click to open clean modal view
        colDef.headerName = 'Analytics Complete';
        colDef.width = 300;
        colDef.wrapText = true;
        colDef.autoHeight = true;
        colDef.cellStyle = { 
          cursor: 'pointer', 
          color: '#0066cc',
          textDecoration: 'underline',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        };
        colDef.valueFormatter = (params) => {
          if (!params.value) return '';
          const jsonStr = typeof params.value === 'string' 
            ? params.value 
            : JSON.stringify(params.value, null, 2);
          // Truncate if too long for display
          return jsonStr.length > 200 
            ? jsonStr.substring(0, 200) + '... (click to view full)' 
            : jsonStr;
        };
      } else if (typeof value === 'object' && value !== null) {
        // Object columns - show as JSON string
        colDef.valueFormatter = (params) => {
          return params.value ? JSON.stringify(params.value) : '';
        };
      } else {
        // All other columns - default text filter with apply/reset buttons
        colDef.filter = 'agTextColumnFilter';
        colDef.filterParams = {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        };
      }
      
      columns.push(colDef);
    });
    
    return columns;
  }, []);

  // Update columns when data loads
  useEffect(() => {
    if (rowData && rowData.length > 0 && activeTab === 0) {
      const generatedColumns = generateColumnsFromData(rowData);
      setColumnDefs(generatedColumns);
    }
  }, [rowData, generateColumnsFromData, activeTab]);

  // Load saved column state from localStorage
  useEffect(() => {
    if (userAttributes?.username && gridRef.current && rowData.length > 0) {
      const savedState = localStorage.getItem(`survey-insights-layout-${userAttributes.username}`);
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          gridRef.current.api.applyColumnState({ state, applyOrder: true });
        } catch (error) {
          console.error('Failed to restore column layout:', error);
        }
      }
    }
  }, [userAttributes, rowData]);

  // Save column state handlers
  const onColumnMoved = useCallback(() => {
    if (gridRef.current && userAttributes?.username) {
      const columnState = gridRef.current.api.getColumnState();
      localStorage.setItem(
        `survey-insights-layout-${userAttributes.username}`,
        JSON.stringify(columnState)
      );
    }
  }, [userAttributes]);

  const onColumnVisible = useCallback(() => {
    if (gridRef.current && userAttributes?.username) {
      const columnState = gridRef.current.api.getColumnState();
      localStorage.setItem(
        `survey-insights-layout-${userAttributes.username}`,
        JSON.stringify(columnState)
      );
    }
  }, [userAttributes]);

  // Toggle column visibility
  const toggleColumnVisibility = (field) => {
    if (gridRef.current) {
      const columnState = gridRef.current.api.getColumnState();
      const column = columnState.find(col => col.colId === field);
      if (column) {
        gridRef.current.api.setColumnsVisible([field], !column.hide);
      }
    }
  };

  // Get all columns for visibility menu
  const getAllColumns = () => {
    if (!columnDefs || columnDefs.length === 0) return [];
    return columnDefs.filter(col => col.field);
  };

  // Check if column is visible
  const isColumnVisible = (field) => {
    if (!gridRef.current) return true;
    const columnState = gridRef.current.api.getColumnState();
    const column = columnState.find(col => col.colId === field);
    return column ? !column.hide : true;
  };

  // Reset column layout
  const resetLayout = () => {
    if (gridRef.current && userAttributes?.username) {
      gridRef.current.api.resetColumnState();
      localStorage.removeItem(`survey-insights-layout-${userAttributes.username}`);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    if (gridRef.current) {
      gridRef.current.api.setFilterModel(null);
    }
  };

  // Fetch filter options and project info when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchFilterOptions();
      fetchProjectInfo();
    } else {
      setProjectInfo(null);
    }
  }, [selectedProject]);

  const fetchProjectInfo = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) return;
      const response = await axios.get(
        `${API_URL}/api/analytics-views/project-info/${selectedProject}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) setProjectInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch project info:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        alert('Authentication required. Please sign in again.');
        navigate('/login');
        return;
      }
      
      const response = await axios.get(
        `${API_URL}/api/analytics-views/filter-options/${selectedProject}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setFilterOptions(response.data.filter_options);
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please sign in again.');
        navigate('/login');
      }
    }
  };

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    if (!selectedProject) return;
    
    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        alert('Authentication required. Please sign in again.');
        navigate('/login');
        setLoading(false);
        return;
      }
      
      const params = new URLSearchParams();
      if (selectedBrand !== 'All') params.append('brand_name', selectedBrand);
      if (selectedCategory !== 'All') params.append('survey_category', selectedCategory);
      if (selectedMoment !== 'All') params.append('occasion_no', selectedMoment);
      
      console.log('🔍 Fetching analytics with params:', Object.fromEntries(params));
      
      const response = await axios.get(
        `${API_URL}/api/analytics-views/insights/${selectedProject}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        console.log('✅ API Response:', { rows: response.data.data?.length, meta: response.data.meta });
        setRowData(response.data.data);
        setLastUpdated(new Date());
      } else {
        console.warn('⚠️ API returned success: false', response.data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please sign in again.');
        navigate('/login');
      } else {
        alert('Failed to load data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Refresh handler
  const handleRefresh = () => {
    if (rowData.length > 0) {
      fetchAnalyticsData();
    }
  };

  // Export functions
  const exportSelected = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    if (selectedRows.length === 0) {
      alert('Please select rows to export');
      return;
    }
    gridRef.current.api.exportDataAsCsv({
      onlySelected: true,
      fileName: `survey-insights-selected-${new Date().toISOString().split('T')[0]}.csv`,
    });
  };

  const exportAll = () => {
    gridRef.current.api.exportDataAsCsv({
      fileName: `survey-insights-all-${new Date().toISOString().split('T')[0]}.csv`,
    });
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/')}>
          <ArrowBackIcon /> Back to Home
        </BackButton>
        <Title>Survey Insights</Title>
      </Header>
      
      <Content>
        <TabContainer>
          <StyledTabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            aria-label="survey insights tabs"
          >
            <Tab label="� Analytics Table" id="tab-0" />
            <Tab label="💬 Insights@GPT" id="tab-1" />
          </StyledTabs>
        </TabContainer>

        {/* Tab 1: Insights@GPT */}
        {activeTab === 1 && (
          <TabPanel>
            <InsightsAtGPT 
              availableProjects={availableProjects}
              defaultProject={selectedProject}
            />
          </TabPanel>
        )}

        {/* Tab 0: Analytics Table */}
        {activeTab === 0 && (
          <TabPanel>
            <ControlPanel>
              <FilterSection>
                <FilterLabel>Project Code:</FilterLabel>
                <FilterSelect 
                  value={selectedProject} 
                  onChange={(e) => setSelectedProject(e.target.value)}
                >
                  <option value="">Select Project</option>
                  {availableProjects.map(project => (
                    <option key={project} value={project}>{project}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Brand:</FilterLabel>
                <FilterSelect 
                  value={selectedBrand} 
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  disabled={!selectedProject}
                >
                  <option value="All">All</option>
                  {filterOptions.brand_names?.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Survey Category:</FilterLabel>
                <FilterSelect 
                  value={selectedCategory} 
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={!selectedProject}
                >
                  <option value="All">All</option>
                  {filterOptions.survey_categories?.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Occasion:</FilterLabel>
                <FilterSelect 
                  value={selectedMoment} 
                  onChange={(e) => setSelectedMoment(e.target.value)}
                  disabled={!selectedProject}
                >
                  <option value="All">All</option>
                  {filterOptions.occasion_nos?.map(moment => (
                    <option key={moment} value={moment}>{moment}</option>
                  ))}
                </FilterSelect>

                {projectInfo && (
                  <ProjectDateBadge>
                    <StatusDot active={projectInfo.status === 'active'} title={projectInfo.status} />
                    <strong>{projectInfo.project_name}</strong>
                    <span>&nbsp;|&nbsp;Study Period:&nbsp;</span>
                    <strong>{String(projectInfo.start_date).substring(0, 10)} 00:00:00</strong>
                    <span>&nbsp;→&nbsp;</span>
                    <strong>{projectInfo.end_date ? `${String(projectInfo.end_date).substring(0, 10)} 23:00:00` : 'Open'}</strong>
                  </ProjectDateBadge>
                )}

                <ActionButton onClick={fetchAnalyticsData} disabled={!selectedProject || loading}>
                  {loading ? 'Loading...' : 'Load Data'}
                </ActionButton>
              </FilterSection>

              <ExportSection>
                <ActionButton onClick={handleRefresh} disabled={rowData.length === 0 || loading} title="Refresh to get latest data">
                  <RefreshIcon /> {loading ? 'Refreshing...' : 'Refresh'}
                </ActionButton>
                <ActionButton onClick={clearAllFilters} disabled={rowData.length === 0} title="Clear all column filters">
                  Clear Filters
                </ActionButton>
                <ActionButton onClick={exportSelected} disabled={rowData.length === 0}>
                  <DownloadIcon /> Export Selected
                </ActionButton>
                <ActionButton onClick={exportAll} disabled={rowData.length === 0}>
                  <DownloadIcon /> Export All
                </ActionButton>
                <ActionButton onClick={resetLayout} $variant="secondary" title="Reset column order and visibility">
                  Reset Layout
                </ActionButton>
              </ExportSection>
            </ControlPanel>

            {lastUpdated && (
              <StatusBar>
                <StatusText>
                  Last updated: {lastUpdated.toLocaleString()} | Total rows: {rowData.length}
                </StatusText>
              </StatusBar>
            )}

            <GridContainer>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                theme={gridTheme}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  floatingFilter: true,
                }}
                pagination={true}
                paginationPageSize={50}
                rowSelection={{ 
                  mode: 'multiRow',
                  checkboxes: true,
                  headerCheckbox: true,
                }}
                onColumnMoved={onColumnMoved}
                onColumnVisible={onColumnVisible}
                animateRows={true}
              />
            </GridContainer>

            {rowData.length === 0 && !loading && (
              <EmptyState>
                <EmptyIcon>📊</EmptyIcon>
                <EmptyText>Select a project and click "Load Data" to view analytics data</EmptyText>
              </EmptyState>
            )}
          </TabPanel>
        )}
      </Content>
    </Container>
  );
};

export default SurveyInsights;

const Container = styled.div`
  min-height: 100vh;
  background-color: ${(props) => props.theme.colors.background};
`;

const Header = styled.header`
  background: linear-gradient(135deg, ${(props) => props.theme.colors.primary} 0%, ${(props) => props.theme.colors.primaryDark} 100%);
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  background: rgba(255, 255, 255, 0.1);
  color: ${(props) => props.theme.colors.textPrimary};
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: ${(props) => props.theme.fontSizes.sm};
  margin-bottom: ${(props) => props.theme.spacing.md};
  transition: all ${(props) => props.theme.transitions.fast};
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Title = styled.h1`
  font-size: ${(props) => props.theme.fontSizes['3xl']};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  color: ${(props) => props.theme.colors.textPrimary};
`;

const Content = styled.main`
  max-width: 95%;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.xl};
`;



// Tab Styles
const TabContainer = styled.div`
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  margin-bottom: ${(props) => props.theme.spacing.xl};
`;

const StyledTabs = styled(Tabs)`
  .MuiTabs-indicator {
    background-color: ${(props) => props.theme.colors.primary} !important;
  }
  
  .MuiTab-root {
    color: ${(props) => props.theme.colors.textSecondary};
    font-weight: 500;
    text-transform: none;
    font-size: ${(props) => props.theme.fontSizes.base};
    
    &.Mui-selected {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const TabPanel = styled.div`
  width: 100%;
`;

// Control Panel Styles
const ControlPanel = styled.div`
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  box-shadow: ${(props) => props.theme.shadows.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.md};
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  color: ${(props) => props.theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: 1px solid ${(props) => props.theme.colors.border};
  background-color: white;
  font-size: ${(props) => props.theme.fontSizes.sm};
  cursor: pointer;
  min-width: 150px;
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const ExportSection = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.sm};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: none;
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  cursor: pointer;
  transition: all ${(props) => props.theme.transitions.fast};
  
  background-color: ${(props) => props.$variant === 'secondary' ? '#6c757d' : props.theme.colors.primary};
  color: white;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProjectDateBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.textSecondary};
  background: ${(props) => props.theme.colors.surface || props.theme.colors.cardBackground};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 4px;
  padding: 4px 10px;
  white-space: nowrap;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.active ? '#22c55e' : '#f59e0b'};
  margin-right: 2px;
  flex-shrink: 0;
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.md};
  margin-bottom: ${(props) => props.theme.spacing.md};
  border: 1px solid ${(props) => props.theme.colors.border};
`;

const StatusText = styled.span`
  font-size: ${(props) => props.theme.fontSizes.sm};
  color: ${(props) => props.theme.colors.textSecondary};
  font-weight: ${(props) => props.theme.fontWeights.medium};
`;

const GridContainer = styled.div`
  height: 600px;
  width: 100%;
  background-color: white;
  border-radius: ${(props) => props.theme.borderRadius.lg};
  box-shadow: ${(props) => props.theme.shadows.md};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(props) => props.theme.spacing['3xl']};
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  margin-top: ${(props) => props.theme.spacing.lg};
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const EmptyText = styled.p`
  font-size: ${(props) => props.theme.fontSizes.lg};
  color: ${(props) => props.theme.colors.textSecondary};
`;
